import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { ArcPage, ArcPageHeader, ArcSelect, ArcTabs } from '../components/arc/primitives/arc-primitives';
import { useFeather } from '../shell/useFeather';
import { confirmDialog, noticeDialog } from '../shell/dialogs';
import { ApiError } from '../api/client';
import {
  actVyuctovani,
  bulkVyuctovani,
  getVyuctovani,
  getVyuctovaniDetail,
  getVyuctovaniPrehled,
  prepareVyuctovani,
  resetVyuctovani,
  testMailVyuctovani,
  vyuctovaniPdfUrl,
  type SettlementCurrency,
  type SettlementOverview,
  type SettlementPeriodState,
  type SettlementPeriodsOverview,
  type SettlementSpace,
  type SettlementStatus,
  type SettlementView,
} from '../api/settlement';
import '../affilmaster/affilmaster-dashboard.css';
import '../affilmaster/affilmaster-prijmy.css';
import './vyuctovani-klienti.css';

const MONTH_CS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];
const STATUS_LABEL: Record<SettlementStatus, string> = { navrh: 'Návrh', schvaleno: 'Potvrzeno', vystaveno: 'Vystaveno', odeslano: 'Odesláno', nefakturuje: 'Nefakturuje se' };
const PERIOD_STATE_LABEL: Record<SettlementPeriodState, string> = { nepripraveno: 'Nepřipraveno', navrh: 'Návrh', schvaleno: 'Potvrzeno', vystaveno: 'Vystaveno', odeslano: 'Odesláno' };
const STEP_ORDER: SettlementStatus[] = ['navrh', 'schvaleno', 'vystaveno', 'odeslano'];

const ymLabel = (ym: string) => { const [y, m] = ym.split('-'); return `${MONTH_CS[Number(m) - 1] ?? m} ${y ?? ''}`; };
const fmtDay = (v: string | null) => (v ? `${Number(v.slice(8, 10))}. ${Number(v.slice(5, 7))}. ${v.slice(0, 4)}` : '-');
const fmtMoney = (v: number, currency: string) => `${v.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency === 'EUR' ? '€' : 'Kč'}`;
const klientu = (n: number) => (n === 1 ? '1 klient' : n >= 2 && n <= 4 ? `${n} klienti` : `${n} klientů`);
const polozek = (n: number) => (n === 1 ? '1 položka' : n >= 2 && n <= 4 ? `${n} položky` : `${n} položek`);
const invoiceFileNameFor = (invoiceId: string) => `Faktura-${invoiceId.replace(/[^A-Za-z0-9_-]/g, '')}.pdf`;
const errText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message || fallback : fallback);

function periodOptions(): string[] {
  const now = new Date();
  const out: string[] = [];
  const d = new Date(now.getFullYear(), now.getMonth(), 1);
  for (let i = 0; i < 12; i += 1) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

export interface VyuctovaniKlientiConfig {
  space: SettlementSpace;
  eyebrow: string;
  manageKey: string;
  currencies: readonly SettlementCurrency[];
}

interface Props { config: VyuctovaniKlientiConfig; detailId?: string; onOpenDetail: (id: string) => void; onCloseDetail: () => void; onOpenClient: (name: string, partnerId: number) => void }

function parseSegment(detailId: string | undefined): { period: string | null; currency: SettlementCurrency | null; settlementId: number | null } {
  if (!detailId) return { period: null, currency: null, settlementId: null };
  if (detailId === 'czk' || detailId === 'eur') return { period: null, currency: detailId === 'eur' ? 'EUR' : 'CZK', settlementId: null };
  const m = /^(\d{4}-\d{2})(?:-(czk|eur))?$/.exec(detailId);
  if (m) return { period: m[1] ?? null, currency: m[2] === 'eur' ? 'EUR' : m[2] === 'czk' ? 'CZK' : null, settlementId: null };
  return { period: null, currency: null, settlementId: /^\d+$/.test(detailId) ? Number(detailId) : null };
}

export function VyuctovaniKlientiPage(props: Props) {
  const segment = parseSegment(props.detailId);
  if (segment.period == null && segment.settlementId == null) {
    return <SettlementPeriods config={props.config} currency={segment.currency ?? 'CZK'} onOpen={props.onOpenDetail} />;
  }
  return <SettlementMonth {...props} />;
}

function SettlementPeriods({ config, currency, onOpen }: { config: VyuctovaniKlientiConfig; currency: SettlementCurrency; onOpen: (segment: string) => void }) {
  const [data, setData] = useState<SettlementPeriodsOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getVyuctovaniPrehled(config.space)
      .then((d) => { if (!cancelled) { setData(d); setErr(null); } })
      .catch((e: unknown) => { if (!cancelled) setErr(errText(e, 'Načtení vyúčtování selhalo')); });
    return () => { cancelled = true; };
  }, [config.space]);
  useFeather([data, err]);

  const rows = data ? data.periods.filter((p) => p.currency === currency) : [];
  const current = data ? rows.find((p) => p.period === data.currentPeriod) ?? null : null;
  const finished = rows.filter((p) => p.state === 'odeslano');
  const finishedNet = finished.reduce((a, p) => a + p.net, 0);

  return (
    <ArcPage className="affms-dash affms-prijmy am-vyuc">
      <ArcPageHeader
        eyebrow={config.eyebrow}
        title="Vyúčtování klientů"
        description="Měsíční fakturace fixních služeb klientům"
        meta={<div className="am-vyuc-head-tools">
          {data && <span className={`am-vyuc-mode ${data.live ? 'is-live' : 'is-sim'}`}>{data.live ? 'Ostrý provoz' : 'Simulace'}</span>}
        </div>}
      />

      {err && (
        <div className="prototype-notice prototype-notice-error" role="alert">
          <span className="am-vyuc-btn-icon" aria-hidden="true"><i data-feather="alert-circle" /></span>
          <div><strong>Vyúčtování se nepodařilo načíst</strong><span>{err}</span></div>
        </div>
      )}
      {!err && !data && <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>}

      {data && (
        <div className="am-vyuc-stack">
          {config.currencies.length > 1 && (
            <div className="prototype-overview-toolbar">
              <ArcTabs
                label="Měna vyúčtování"
                value={currency}
                onChange={(next) => onOpen(next.toLowerCase())}
                items={config.currencies.map((item) => ({ value: item, label: `Vyúčtování ${item}` }))}
              />
            </div>
          )}

          <article className="prototype-panel am-vyuc-summary" aria-label="Souhrn vyúčtování">
            <div><span>Aktuální období</span><strong>{ymLabel(data.currentPeriod)}</strong><small>{current ? `${PERIOD_STATE_LABEL[current.state]}${current.prepared ? ` · ${current.progress} % hotovo` : ''}` : 'v této měně není co fakturovat'}</small></div>
            <div><span>K fakturaci bez DPH</span><strong>{fmtMoney(current?.net ?? 0, currency)}</strong><small>s DPH {fmtMoney(current?.gross ?? 0, currency)}</small></div>
            <div><span>Klienti a položky</span><strong>{klientu(current?.clients ?? 0)}</strong><small>{polozek(current?.items ?? 0)}</small></div>
            <div><span>Dokončená období</span><strong>{finished.length} z {rows.length}</strong><small>rozesláno {fmtMoney(finishedNet, currency)} bez DPH</small></div>
          </article>

          <div className="prototype-panel aff-finance-history-shell am-prijmy-history-shell">
            <div className="table-responsive-mobile">
              <table className="table table-hover align-middle aff-list-table aff-finance-history-table am-prijmy-history-table am-vyuc-periods mb-0" aria-label={`Vyúčtování ${currency} po měsících`}>
                <thead>
                  <tr>
                    <th className="ps-3">Období</th>
                    <th className="text-end">Klientů</th>
                    <th className="text-end">Položek</th>
                    <th className="text-end">Bez DPH</th>
                    <th className="text-end">S DPH</th>
                    <th>Postup</th>
                    <th>Stav</th>
                    <th className="text-end pe-3" style={{ width: 40 }} aria-label="Detail"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? <tr><td colSpan={8} className="text-center py-4 text-muted">V této měně zatím není žádné vyúčtování.</td></tr>
                    : rows.map((r) => (
                      <tr key={r.period} className="aff-row" onClick={() => onOpen(`${r.period}-${currency.toLowerCase()}`)}>
                        <td className="ps-3">{ymLabel(r.period)}</td>
                        <td className="text-end">{r.clients}</td>
                        <td className="text-end">{r.items}</td>
                        <td className="text-end">{fmtMoney(r.net, currency)}</td>
                        <td className="text-end">{fmtMoney(r.gross, currency)}</td>
                        <td>
                          <div className="am-vyuc-progress-cell">
                            <div className="am-vyuc-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={r.progress}><span style={{ width: `${r.progress}%` }} /></div>
                            <small>{r.progress} %</small>
                          </div>
                        </td>
                        <td><span className={`am-vyuc-status is-${r.state}`}>{PERIOD_STATE_LABEL[r.state]}{r.simulated ? ' · simulace' : ''}</span></td>
                        <td className="prototype-row-arrow pe-3" aria-hidden="true"><span className="feather-icon"><i data-feather="chevron-right" /></span></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </ArcPage>
  );
}

function SettlementMonth({ config, detailId, onOpenDetail, onCloseDetail, onOpenClient }: Props) {
  const { me } = useAuth();
  const { space } = config;
  const canManage = !!me?.permissionKeys?.includes(config.manageKey);
  const options = useMemo(periodOptions, []);
  const segment = parseSegment(detailId);
  const [last, setLast] = useState<{ period: string; currency: SettlementCurrency }>(() => ({ period: segment.period ?? options[1] ?? options[0] ?? '', currency: segment.currency ?? 'CZK' }));
  const ym = segment.period ?? last.period;
  const currency = segment.period ? (segment.currency ?? 'CZK') : last.currency;
  const [data, setData] = useState<SettlementOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string; items?: string[] } | null>(null);
  const [picked, setPicked] = useState<number[]>([]);
  const [checked, setChecked] = useState(false);

  useEffect(() => { if (segment.period) setLast({ period: segment.period, currency: segment.currency ?? 'CZK' }); }, [segment.period, segment.currency]);
  const go = useCallback((period: string, cur: SettlementCurrency) => { setLast({ period, currency: cur }); onOpenDetail(`${period}-${cur.toLowerCase()}`); }, [onOpenDetail]);

  const load = useCallback(() => {
    let cancelled = false;
    getVyuctovani(space, ym)
      .then((d) => { if (!cancelled) { setData(d); setErr(null); } })
      .catch((e: unknown) => { if (!cancelled) setErr(errText(e, 'Načtení vyúčtování selhalo')); });
    return () => { cancelled = true; };
  }, [space, ym]);

  useEffect(() => { setData(null); return load(); }, [load]);
  useEffect(() => { setPicked([]); setChecked(false); setNotice(null); }, [ym, currency]);
  useFeather([data, detailId, notice, busy, currency]);

  async function run(key: string, fn: () => Promise<SettlementOverview | { overview: SettlementOverview; done?: number; failed?: { partner: string; error: string }[]; rows?: number }>, okText: (r: { done?: number; rows?: number }) => string) {
    setBusy(key); setNotice(null);
    try {
      const r = await fn();
      const overview = 'overview' in r ? r.overview : r;
      setData(overview);
      setPicked([]); setChecked(false);
      const failed = 'failed' in r && r.failed ? r.failed : [];
      if (failed.length) setNotice({ kind: 'error', text: `Dokončeno ${('done' in r ? r.done : 0) ?? 0}, nepodařilo se ${failed.length}`, items: failed.map((f) => f.error) });
      else setNotice({ kind: 'ok', text: okText('overview' in r ? r : {}) });
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e, 'Akce selhala') });
    } finally { setBusy(null); }
  }

  async function rowAction(row: SettlementView, action: 'approve' | 'unapprove' | 'issue' | 'send') {
    setBusy(`${action}-${row.id}`); setNotice(null);
    try {
      await actVyuctovani(space, row.id, action);
      setData(await getVyuctovani(space, ym));
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e, 'Akce selhala') });
      try { setData(await getVyuctovani(space, ym)); } catch { setErr('Načtení vyúčtování selhalo'); }
    } finally { setBusy(null); }
  }

  if (segment.settlementId != null) {
    return (
      <SettlementDetail
        space={space}
        id={segment.settlementId}
        canManage={canManage}
        live={data ? data.live : null}
        mailFrom={data?.mailFrom ?? null}
        onBack={(row) => (row ? onOpenDetail(`${row.period}-${row.currency.toLowerCase()}`) : onCloseDetail())}
        onChanged={load}
        onOpenClient={onOpenClient}
      />
    );
  }

  const live = !!data?.live;
  const modeWord = live ? 'ostře' : 'v simulaci';
  const process = data?.processes.find((p) => p.currency === currency) ?? null;
  const steps = process?.steps ?? null;
  const billed = data ? data.rows.filter((r) => r.status !== 'nefakturuje' && r.currency === currency) : [];
  const skipped = data ? data.rows.filter((r) => r.status === 'nefakturuje') : [];
  const drafts = billed.filter((r) => r.status === 'navrh');
  const progress = steps && steps.total ? Math.round(((steps.schvaleno + steps.vystaveno * 2 + steps.odeslano * 3) / (steps.total * 3)) * 100) : 0;
  const reached = (idx: number) => {
    if (!steps || !steps.total) return 0;
    const counts = [steps.total, steps.schvaleno + steps.vystaveno + steps.odeslano, steps.vystaveno + steps.odeslano, steps.odeslano];
    return counts[idx] ?? 0;
  };
  const hasSimulated = billed.some((r) => r.simulated || r.status === 'schvaleno');
  const subject = billed.find((r) => r.mailPreview)?.mailPreview?.subject ?? null;
  const changes = billed.reduce((a, r) => a + r.items.filter((i) => i.change).length + r.removedItems.length, 0);
  const toggle = (id: number) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const allPicked = drafts.length > 0 && drafts.every((r) => picked.includes(r.id));

  let primary: { key: string; label: string; icon: string; disabled?: boolean; run: () => void } | null = null;
  if (data && canManage) {
    if (!data.prepared) {
      primary = { key: 'prepare', label: 'Připravit návrhy faktur', icon: 'file-plus', run: () => void run('prepare', () => prepareVyuctovani(space, ym), () => 'Návrhy faktur jsou připravené ke kontrole') };
    } else if (steps && steps.navrh > 0) {
      primary = {
        key: 'approve', label: `Potvrdit vybrané (${picked.length})`, icon: 'check-circle', disabled: picked.length === 0 || !checked,
        run: () => void run('approve', () => bulkVyuctovani(space, ym, 'approve', currency, picked), (r) => `Potvrzeno ${r.done ?? 0} vyúčtování`),
      };
    } else if (steps && steps.schvaleno > 0) {
      primary = {
        key: 'issue', label: `Vystavit v UOL (${steps.schvaleno})`, icon: 'send',
        run: () => void confirmDialog({
          title: 'Vystavit faktury v UOL?',
          message: live
            ? `Počet faktur k ostrému vystavení v UOL: ${steps.schvaleno}. Tento krok nejde vrátit.`
            : `Simulace, počet faktur: ${steps.schvaleno}. Jen se označí jako vystavené, do UOL se nic nezapíše.`,
          confirmLabel: 'Vystavit',
        }).then((ok) => { if (ok) void run('issue', () => bulkVyuctovani(space, ym, 'issue', currency), (r) => `Vystaveno ${r.done ?? 0} faktur ${modeWord}`); }),
      };
    } else if (steps && steps.vystaveno > 0) {
      primary = {
        key: 'send', label: `Rozeslat e-maily (${steps.vystaveno})`, icon: 'mail',
        run: () => void confirmDialog({
          title: 'Rozeslat faktury klientům?',
          message: live
            ? `Počet e-mailů s fakturou, které odejdou klientům: ${steps.vystaveno}.`
            : `Simulace, počet e-mailů: ${steps.vystaveno}. Jen se zaznamenají, klientům nic neodejde.`,
          confirmLabel: 'Rozeslat',
        }).then((ok) => { if (ok) void run('send', () => bulkVyuctovani(space, ym, 'send', currency), (r) => `Rozesláno ${r.done ?? 0} e-mailů ${modeWord}`); }),
      };
    }
  }

  return (
    <ArcPage className="affms-dash affms-prijmy am-vyuc">
      <header className="prototype-page-head am-prijmy-page-head">
        <div>
          <button type="button" className="am-vyuc-back" onClick={() => (config.currencies.length > 1 ? onOpenDetail(currency.toLowerCase()) : onCloseDetail())}><i data-feather="arrow-left" />{config.currencies.length > 1 ? `Zpět na Vyúčtování ${currency}` : 'Zpět na vyúčtování'}</button>
          <h1>Vyúčtování · {ymLabel(ym)}</h1>
          <p>Měsíční fakturace fixních služeb klientům · {currency}</p>
        </div>
        <div className="am-vyuc-head-tools">
          {data && <span className={`am-vyuc-mode ${live ? 'is-live' : 'is-sim'}`}>{live ? 'Ostrý provoz' : 'Simulace'}</span>}
          <ArcSelect
            label="Období"
            value={ym}
            onChange={(value) => go(value, currency)}
            options={options.map((option) => ({ value: option, label: ymLabel(option) }))}
          />
        </div>
      </header>

      {err && (
        <div className="prototype-notice prototype-notice-error" role="alert">
          <span className="am-vyuc-btn-icon" aria-hidden="true"><i data-feather="alert-circle" /></span>
          <div><strong>Vyúčtování se nepodařilo načíst</strong><span>{err}</span></div>
        </div>
      )}
      {!err && !data && <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>}

      {data && (
        <div className="am-vyuc-stack">
          <article className="prototype-panel am-vyuc-process" aria-label="Postup vyúčtování">
            <div className="am-vyuc-process-head">
              <div>
                <span className="am-vyuc-kicker">POSTUP · {ymLabel(ym).toUpperCase()} · {currency}</span>
                <strong>{!data.prepared ? 'Návrhy zatím nejsou připravené' : steps && steps.total === 0 ? 'V této měně není co fakturovat' : `${progress} % hotovo`}</strong>
                <p>{live ? 'Faktury se vystavují v UOL a e-maily odcházejí klientům.' : 'Simulace: do UOL se nic nezapisuje a klientům nic neodchází.'}</p>
              </div>
              <div className="am-vyuc-process-actions">
                {primary && (
                  <button type="button" key={primary.key} className="btn btn-primary prototype-primary-btn" disabled={busy != null || !!primary.disabled} onClick={primary.run}>
                    <span className="am-vyuc-btn-icon" aria-hidden="true"><i data-feather={primary.icon} /></span>{busy === primary.key ? 'Pracuji…' : primary.label}
                  </button>
                )}
                {canManage && data.prepared && steps && steps.navrh > 0 && (
                  <button type="button" className="btn prototype-secondary-btn" disabled={busy != null} onClick={() => void run('prepare', () => prepareVyuctovani(space, ym), () => 'Návrhy přepočítané podle aktuálních dealů')}>
                    <span className="am-vyuc-btn-icon" aria-hidden="true"><i data-feather="refresh-cw" /></span>Přepočítat návrhy
                  </button>
                )}
                {canManage && !live && hasSimulated && (
                  <button type="button" className="btn prototype-secondary-btn" disabled={busy != null} onClick={() => { void confirmDialog({ title: 'Vrátit simulaci?', message: 'Simulovaná vyúčtování se vrátí zpět do stavu návrhů.', confirmLabel: 'Vrátit' }).then((ok) => { if (ok) void run('reset', () => resetVyuctovani(space, ym, currency), (r) => `Simulace vrácena, ${r.rows ?? 0} vyúčtování zpět v návrhu`); }); }}>
                    <span className="am-vyuc-btn-icon" aria-hidden="true"><i data-feather="rotate-ccw" /></span>Vrátit simulaci
                  </button>
                )}
              </div>
            </div>
            <div className="am-vyuc-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}><span style={{ width: `${progress}%` }} /></div>
            <ol className="am-vyuc-steps">
              {STEP_ORDER.map((st, idx) => {
                const done = !!steps && steps.total > 0 && reached(idx) === steps.total && data.prepared;
                const active = !done && steps != null && (idx === 0 ? !data.prepared : reached(idx - 1) > reached(idx) || reached(idx) > 0);
                return (
                  <li key={st} className={done ? 'is-done' : active ? 'is-active' : ''}>
                    <span className="am-vyuc-step-dot">{done ? '✓' : idx + 1}</span>
                    <div>
                      <strong>{['Návrh faktur', 'Kontrola a potvrzení', 'Vystavení v UOL', 'Rozeslání klientům'][idx]}</strong>
                      <small>{steps && steps.total ? `${reached(idx)} z ${steps.total}` : 'čeká'}</small>
                    </div>
                  </li>
                );
              })}
            </ol>
            {canManage && steps && steps.navrh > 0 && (
              <label className="am-vyuc-confirm">
                <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
                <span>Zkontroloval jsem klienty, položky a částky vybraných vyúčtování{changes > 0 ? `, včetně změn oproti minulému měsíci (${changes})` : ''}.</span>
              </label>
            )}
          </article>

          {notice && (
            <div key={`${notice.kind}-${notice.text}`} className={`prototype-notice ${notice.kind === 'error' ? 'prototype-notice-error' : 'am-vyuc-notice-ok'}`} role="status">
              <span className="am-vyuc-btn-icon" aria-hidden="true"><i data-feather={notice.kind === 'error' ? 'alert-circle' : 'check-circle'} /></span>
              <div><strong>{notice.text}</strong>{notice.items?.map((t) => <span key={t}>{t}</span>)}</div>
            </div>
          )}

          {data.prepared && process && (
            <article className="prototype-panel am-vyuc-summary">
              <div><span>Faktur</span><strong>{process.steps.total}</strong><small>{process.steps.vystaveno + process.steps.odeslano} vystaveno · {process.steps.odeslano} odesláno</small></div>
              <div><span>Bez DPH</span><strong>{fmtMoney(process.net, currency)}</strong><small>s DPH {fmtMoney(process.gross, currency)}</small></div>
              <div><span>Předmět e-mailu</span><strong className="am-vyuc-summary-text">{subject ?? '-'}</strong><small>{data.mailCopy ? `kopie každého e-mailu na ${data.mailCopy}` : data.mailFrom ? `odesílatel ${data.mailFrom}` : 'odesílatel není nastavený'}</small></div>
              <div><span>Uhrazeno</span><strong>{process.paid} z {process.steps.total}</strong><small>podle plateb v UOL</small></div>
            </article>
          )}

          {billed.length > 0 && (
            <section className="am-prijmy-section" aria-labelledby="am-vyuc-list-title">
              <div className="am-prijmy-section-head">
                <div><span>FAKTURY · {currency}</span><h2 id="am-vyuc-list-title">Klienti k fakturaci</h2></div>
                <p>DUZP {fmtDay(billed[0]?.duzp ?? null)} · vystavení {fmtDay(billed[0]?.issueDate ?? null)} · splatnost {data.dueDays} dní</p>
              </div>
              {canManage && drafts.length > 0 && (
                <label className="am-vyuc-pick-all">
                  <input type="checkbox" checked={allPicked} onChange={() => setPicked(allPicked ? [] : drafts.map((r) => r.id))} />
                  <span>Vybrat všechny návrhy ({drafts.length})</span>
                </label>
              )}
              <ol className="am-vyuc-list">
                {billed.map((r) => (
                  <li key={r.id} className={`am-vyuc-row is-${r.status}`}>
                    <span className="am-vyuc-row-pick">
                      {canManage && r.status === 'navrh' && <input type="checkbox" aria-label={`Vybrat ${r.partnerName}`} checked={picked.includes(r.id)} onChange={() => toggle(r.id)} />}
                    </span>
                    <div className="am-vyuc-row-body">
                      <div className="am-vyuc-row-top">
                        <span className={`market-flag market-flag-${r.market}`}>{r.market.toUpperCase()}</span>
                        <button type="button" className="am-vyuc-row-client" onClick={() => onOpenClient(r.partnerName, r.partnerId)} title="Otevřít kartu klienta a dealy">{r.partnerName}</button>
                        <span className="am-vyuc-row-company">{r.uolCompanyName ?? ''}</span>
                        <span className="am-vyuc-row-total">{fmtMoney(r.totalNet, r.currency)}<small>{r.vatRate ? `s DPH ${fmtMoney(r.totalGross, r.currency)}` : 'DPH 0 %'}</small></span>
                      </div>
                      <ul className="am-vyuc-row-items">
                        {r.items.map((i) => (
                          <li key={i.dealId}>
                            <button type="button" onClick={() => onOpenClient(r.partnerName, r.partnerId)} title="Zkontrolovat deal na kartě klienta">{i.description}</button>
                            {i.change === 'new' && <em className="is-new">nové oproti minulému měsíci</em>}
                            {i.change === 'changed' && <em className="is-changed">změna z {fmtMoney(i.previousAmount ?? 0, r.currency)}</em>}
                            <strong>{fmtMoney(i.amount, r.currency)}</strong>
                          </li>
                        ))}
                        {r.removedItems.map((i) => (
                          <li key={`removed-${i.title}`} className="is-removed"><span>{i.title}</span><em className="is-changed">oproti minulému měsíci chybí</em><strong>{fmtMoney(i.amount, r.currency)}</strong></li>
                        ))}
                      </ul>
                      <div className="am-vyuc-row-meta">
                        <span>{r.uolInvoiceId ? `Faktura ${r.uolInvoiceId}` : 'Faktura zatím nevystavena'}</span>
                        <span>{r.status === 'odeslano' ? `Odesláno na ${r.mailTo ?? '-'}${r.mailCc ? `, kopie ${r.mailCc}` : ''}` : r.recipients.to ? `Půjde na ${r.recipients.to}${r.recipients.cc.length ? `, kopie ${r.recipients.cc.join(', ')}` : ''}` : 'Bez příjemce'}</span>
                        {r.warnings.map((w) => <em key={w}>{w}</em>)}
                        {r.paid && <em className="is-paid">Uhrazeno</em>}
                      </div>
                    </div>
                    <span className={`am-vyuc-status is-${r.status}`}>{STATUS_LABEL[r.status]}{r.simulated ? ' · simulace' : ''}</span>
                    <span className="am-vyuc-row-actions">
                      {canManage && r.status === 'schvaleno' && <button type="button" disabled={busy != null} onClick={() => void rowAction(r, 'unapprove')} title={`Vrátit do návrhu ${r.partnerName}`} aria-label={`Vrátit do návrhu ${r.partnerName}`}><i data-feather="corner-up-left" /></button>}
                      <button type="button" onClick={() => onOpenDetail(String(r.id))} title="Detail vyúčtování" aria-label={`Detail ${r.partnerName}`}><i data-feather="chevron-right" /></button>
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {skipped.length > 0 && (
            <section className="am-prijmy-section" aria-labelledby="am-vyuc-skip-title">
              <div className="am-prijmy-section-head">
                <div><span>BEZ FAKTURY</span><h2 id="am-vyuc-skip-title">Nefakturuje se</h2></div>
                <p>{skipped.length} klientů</p>
              </div>
              <ul className="am-vyuc-skip">
                {skipped.map((r) => <li key={r.id}><span className={`market-flag market-flag-${r.market}`}>{r.market.toUpperCase()}</span><strong>{r.partnerName}</strong><span>{r.skipReason ?? 'Bez účtovatelných položek'}</span></li>)}
              </ul>
            </section>
          )}
        </div>
      )}
    </ArcPage>
  );
}

function SettlementDetail({ space, id, canManage, live, mailFrom, onBack, onChanged, onOpenClient }: { space: SettlementSpace; id: number; canManage: boolean; live: boolean | null; mailFrom: string | null; onBack: (row: SettlementView | null) => void; onChanged: () => void; onOpenClient: (name: string, partnerId: number) => void }) {
  const [row, setRow] = useState<SettlementView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!Number.isInteger(id) || id <= 0) { setErr('Vyúčtování nenalezeno'); return undefined; }
    getVyuctovaniDetail(space, id)
      .then((d) => { if (!cancelled) { setRow(d); setErr(null); } })
      .catch((e: unknown) => { if (!cancelled) setErr(errText(e, 'Načtení detailu selhalo')); });
    return () => { cancelled = true; };
  }, [space, id]);
  useFeather([row, err, actionErr, busy]);

  async function testMail() {
    if (!row?.recipients.copy) return;
    if (!(await confirmDialog({ title: 'Poslat zkušební e-mail?', message: `E-mail odejde jen na ${row.recipients.copy}. Klient nic nedostane a stav vyúčtování se nezmění.`, confirmLabel: 'Poslat zkoušku' }))) return;
    setBusy(true); setActionErr(null);
    try {
      const r = await testMailVyuctovani(space, row.id);
      void noticeDialog({ title: 'Zkušební e-mail odeslán', message: `Odesláno na ${r.to}${r.attachment ? ` s přílohou ${r.attachment}.` : ' bez přílohy, faktura zatím není vystavená v UOL.'}` });
    } catch (e) { setActionErr(errText(e, 'Zkušební e-mail se nepodařilo odeslat')); }
    finally { setBusy(false); }
  }

  async function act(action: 'approve' | 'unapprove' | 'issue' | 'send') {
    if (!row) return;
    const simulation = live === false;
    if (action === 'issue' && !(await confirmDialog({
      title: 'Vystavit fakturu v UOL?',
      message: simulation
        ? `Simulace: faktura pro ${row.partnerName} se jen označí jako vystavená, do UOL se nic nezapíše.`
        : `Faktura pro ${row.partnerName} se vystaví v UOL. Tento krok nejde vrátit.`,
      confirmLabel: 'Vystavit',
    }))) return;
    if (action === 'send' && !(await confirmDialog({
      title: 'Odeslat fakturu klientovi?',
      message: simulation
        ? `Simulace: e-mail pro ${row.partnerName} se jen zaznamená, klientovi nic neodejde.`
        : `Klientovi ${row.partnerName} odejde e-mail s fakturou.`,
      confirmLabel: 'Odeslat',
    }))) return;
    setBusy(true); setActionErr(null);
    try { setRow(await actVyuctovani(space, row.id, action)); onChanged(); }
    catch (e) { setActionErr(errText(e, 'Akce selhala')); }
    finally { setBusy(false); }
  }

  return (
    <ArcPage className="affms-dash affms-prijmy am-vyuc">
      <header className="prototype-page-head am-prijmy-page-head">
        <div>
          <button type="button" className="am-vyuc-back" onClick={() => onBack(row)}><i data-feather="arrow-left" />Zpět na vyúčtování</button>
          <h1>{row ? row.partnerName : 'Vyúčtování'}</h1>
          <p>{row ? `Fixní služby · ${ymLabel(row.period)}${row.uolCompanyName ? ` · ${row.uolCompanyName}` : ''}` : 'Detail vyúčtování'}</p>
        </div>
        {row && (
          <div className="am-vyuc-head-tools">
            {!row.simulated && row.uolInvoiceId && row.uolInvoiceId !== 'PENDING' && (
              <a className="btn prototype-secondary-btn" href={vyuctovaniPdfUrl(space, row.id)} target="_blank" rel="noreferrer">Faktura {row.uolInvoiceId} PDF</a>
            )}
            <button type="button" className="btn prototype-secondary-btn" onClick={() => onOpenClient(row.partnerName, row.partnerId)}>Karta klienta a dealy</button>
            <span className={`am-vyuc-status is-${row.status}`}>{STATUS_LABEL[row.status]}{row.simulated ? ' · simulace' : ''}</span>
          </div>
        )}
      </header>
      {err && <div className="prototype-notice prototype-notice-error" role="alert"><i data-feather="alert-circle" /><div><strong>Detail se nepodařilo načíst</strong><span>{err}</span></div></div>}
      {!err && !row && <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>}
      {row && (
        <div className="am-vyuc-stack">
          {actionErr && <div className="prototype-notice prototype-notice-error" role="alert"><i data-feather="alert-circle" /><div><strong>{actionErr}</strong></div></div>}
          {row.warnings.length > 0 && <div className="prototype-notice prototype-notice-error" role="status"><i data-feather="alert-triangle" /><div><strong>Před ostrým během doplnit</strong>{row.warnings.map((w) => <span key={w}>{w}</span>)}</div></div>}

          <article className="prototype-panel am-vyuc-summary">
            <div><span>Bez DPH</span><strong>{fmtMoney(row.totalNet, row.currency)}</strong><small>DPH {row.vatRate} %</small></div>
            <div><span>K úhradě</span><strong>{fmtMoney(row.totalGross, row.currency)}</strong><small>účet {row.bankAccount}</small></div>
            <div><span>DUZP</span><strong>{fmtDay(row.duzp)}</strong><small>vystavení {fmtDay(row.issueDate)}</small></div>
            <div><span>Splatnost</span><strong>{fmtDay(row.dueDate)}</strong><small>{row.uolInvoiceId ? `faktura ${row.uolInvoiceId}` : 'faktura zatím nevystavena'}{row.paid ? ' · uhrazeno' : ''}</small></div>
          </article>

          {canManage && row.status !== 'nefakturuje' && (row.status !== 'odeslano' || !!row.recipients.copy) && (
            <div className="am-vyuc-detail-actions">
              {row.status === 'navrh' && <button type="button" className="btn btn-primary prototype-primary-btn" disabled={busy} onClick={() => void act('approve')}><i data-feather="check-circle" />Potvrdit vyúčtování</button>}
              {row.status === 'schvaleno' && <button type="button" className="btn btn-primary prototype-primary-btn" disabled={busy} onClick={() => void act('issue')}><i data-feather="send" />Vystavit v UOL</button>}
              {row.status === 'schvaleno' && <button type="button" className="btn prototype-secondary-btn" disabled={busy} onClick={() => void act('unapprove')}><i data-feather="corner-up-left" />Vrátit do návrhu</button>}
              {row.status === 'vystaveno' && <button type="button" className="btn btn-primary prototype-primary-btn" disabled={busy} onClick={() => void act('send')}><i data-feather="mail" />Odeslat klientovi</button>}
              {row.recipients.copy && <button type="button" className="btn prototype-secondary-btn" disabled={busy} onClick={() => void testMail()}><i data-feather="at-sign" />Poslat zkušební e-mail</button>}
            </div>
          )}

          <section className="am-prijmy-section" aria-labelledby="am-vyuc-items-title">
            <div className="am-prijmy-section-head"><div><span>FAKTURA</span><h2 id="am-vyuc-items-title">Položky</h2></div><p>{polozek(row.items.length)} · spotřeba {row.consumptionCountry}</p></div>
            {row.items.length === 0 ? <div className="prototype-empty-contact">{row.skipReason ?? 'Bez položek'}</div> : (
              <ol className="am-vyuc-items">
                {row.items.map((i) => (
                  <li key={i.dealId}><span>{i.description}{i.change === 'new' && <em className="am-vyuc-change is-new">nové</em>}{i.change === 'changed' && <em className="am-vyuc-change is-changed">změna z {fmtMoney(i.previousAmount ?? 0, row.currency)}</em>}</span><code>{i.productId}</code><span>DPH {i.vatRate} %</span><strong>{fmtMoney(i.amount, row.currency)}</strong></li>
                ))}
              </ol>
            )}
          </section>

          <div className="am-vyuc-detail-cols">
            <section className="am-prijmy-section" aria-labelledby="am-vyuc-mail-title">
              <div className="am-prijmy-section-head"><div><span>E-MAIL</span><h2 id="am-vyuc-mail-title">Průvodní e-mail</h2></div><p>{mailFrom ? `odesílatel ${mailFrom}` : 'odesílatel není nastavený'}</p></div>
              <article className="prototype-panel am-vyuc-mail">
                <dl>
                  <div><dt>Komu</dt><dd>{row.mailTo ?? row.recipients.to ?? 'chybí fakturační e-mail'}</dd></div>
                  <div><dt>Kopie</dt><dd>{row.mailCc ?? ([...row.recipients.cc, ...(row.recipients.copy ? [row.recipients.copy] : [])].join(', ') || '-')}</dd></div>
                  <div><dt>Předmět</dt><dd>{row.mailSubject ?? row.mailPreview?.subject ?? '-'}</dd></div>
                  <div><dt>Příloha</dt><dd>{!row.simulated && row.uolInvoiceId && row.uolInvoiceId !== 'PENDING' ? <a href={vyuctovaniPdfUrl(space, row.id)} target="_blank" rel="noreferrer">{invoiceFileNameFor(row.uolInvoiceId)}</a> : 'PDF faktury z UOL'}</dd></div>
                </dl>
                <pre>{row.mailPreview?.bodyPlain ?? ''}</pre>
              </article>
            </section>
            <section className="am-prijmy-section" aria-labelledby="am-vyuc-uol-title">
              <div className="am-prijmy-section-head"><div><span>UOL</span><h2 id="am-vyuc-uol-title">Data pro vystavení</h2></div><p>{row.uolContactId ? `kontakt ${row.uolContactId}` : 'klient není napojený na UOL'}</p></div>
              <article className="prototype-panel am-vyuc-payload"><pre>{row.uolPayload ? JSON.stringify(row.uolPayload, null, 2) : 'Bez dat'}</pre></article>
            </section>
          </div>

          <section className="am-prijmy-section" aria-labelledby="am-vyuc-log-title">
            <div className="am-prijmy-section-head"><div><span>PRŮBĚH</span><h2 id="am-vyuc-log-title">Historie</h2></div></div>
            <ul className="am-vyuc-skip">
              <li><strong>Potvrzeno</strong><span>{row.approvedAt ? row.approvedAt.replace('T', ' ') : 'zatím ne'}</span></li>
              <li><strong>Vystaveno</strong><span>{row.issuedAt ? `${row.issuedAt.replace('T', ' ')}${row.simulated ? ' · simulace' : ''}` : 'zatím ne'}</span></li>
              <li><strong>Odesláno</strong><span>{row.sentAt ? row.sentAt.replace('T', ' ') : 'zatím ne'}</span></li>
            </ul>
          </section>
        </div>
      )}
    </ArcPage>
  );
}
